import cn from "classnames";
import { Checkbox, Field, Select } from "components/shared/form";
import Loader from "components/shared/loader";
import { featureModuleTypesEnum } from "helpers/enums";
import { serializeReq, validateForm } from "helpers/form";
import policy from "policies";
import React, { useEffect, useState } from "react";
import * as source from "sources/organizationFeatureModules";
import { config } from "./config";

const getModuleSettings = (moduleType, organizationId) => {
    switch (moduleType) {
        case featureModuleTypesEnum.Waybills:
            return source.getBillsSettings(organizationId);
        case featureModuleTypesEnum.PlanningCarSchedule:
            return source.getPlanningScheduleSettings(organizationId);
        case featureModuleTypesEnum.RefuelingPlanning:
            return source.getRefuelingSettings(organizationId);
        case featureModuleTypesEnum.AxisLoadMonitoring:
            return source.getAxisLoadMonitoringSettings(organizationId);
        case featureModuleTypesEnum.DriverControlPoints:
            return source.getDriverControlPointsSettings(organizationId);
        case featureModuleTypesEnum.DriverEducationResources:
            return source.getDriverEducationResourceSettings(organizationId);
        case featureModuleTypesEnum.Exploitation:
            return source.getExploitationSettings(organizationId);
        case featureModuleTypesEnum.TripScenarios:
            return source.getTripScenariosSettings(organizationId);
        case featureModuleTypesEnum.BidPointsAdjustment:
            return source.getNeuralNetworkSettings(organizationId);
        case featureModuleTypesEnum.Messenger:
            return source.getMessengerSettings(organizationId);
        case featureModuleTypesEnum.DocumentAssignment:
            return source.getDocumentAssignmentSettings(organizationId);
        case featureModuleTypesEnum.TrackerLogsFiltering:
            return source.getTrackerLogsFilteringSettings(organizationId);
        case featureModuleTypesEnum.RatingOfDriversAndLogists:
            return source.getRatingOfDriversAndLogistsSettings(organizationId);
        case featureModuleTypesEnum.Forwarding:
            return source.getForwardingSettings(organizationId);
        case featureModuleTypesEnum.ReportSnapshot:
            return source.getReportSnapshotSettings(organizationId);
        case featureModuleTypesEnum.CrStart:
            return source.getCrStartSettings(organizationId);
        default:
            return Promise.resolve({});
    }
};

const saveModuleSettings = (moduleType, organizationId, data) => {
    switch (moduleType) {
        case featureModuleTypesEnum.Waybills:
            return source.editBillsSettings(organizationId, data);
        case featureModuleTypesEnum.PlanningCarSchedule:
            return source.editPlanningScheduleSettings(organizationId, data);
        case featureModuleTypesEnum.RefuelingPlanning:
            return source.editRefuelingSettings(organizationId, data);
        case featureModuleTypesEnum.AxisLoadMonitoring:
            return source.editAxisLoadMonitoringSettings(organizationId, data);
        case featureModuleTypesEnum.DriverControlPoints:
            return source.editDriverControlPointsSettings(organizationId, data);
        case featureModuleTypesEnum.DriverEducationResources:
            return source.editDriverEducationResourceSettings(organizationId, data);
        case featureModuleTypesEnum.Exploitation:
            return source.editExploitationSettings(organizationId, data);
        case featureModuleTypesEnum.TripScenarios:
            return source.editTripScenariosSettings(organizationId, data);
        case featureModuleTypesEnum.BidPointsAdjustment:
            return source.editUnloadPointAdjustmentSettings(organizationId, data);
        case featureModuleTypesEnum.Messenger:
            return source.editMessengerSettings(organizationId, data);
        case featureModuleTypesEnum.DocumentAssignment:
            return source.editDocumentAssignmentSettings(organizationId, data);
        case featureModuleTypesEnum.TrackerLogsFiltering:
            return source.editTrackerLogsFilteringSettings(organizationId, data);
        case featureModuleTypesEnum.RatingOfDriversAndLogists:
            return source.editRatingOfDriversAndLogistsSettings(organizationId, data);
        case featureModuleTypesEnum.Forwarding:
            return source.editForwardingSettings(organizationId, data);
        case featureModuleTypesEnum.ReportSnapshot:
            return source.editReportSnapshotSettings(organizationId, data);
        case featureModuleTypesEnum.CrStart:
            return source.editCrStartSettings(organizationId, data);
        default:
            return Promise.resolve({});
    }
};

const FeatureModuleForm = (props) => {
    const [isLoading, setLoading] = useState(true);
    const [formData, setFormData] = useState({});
    const [isValid, setIsValid] = useState(true);
    const { roles } = props;
    useEffect(() => {
        getModuleSettings(props.featureModuleType, props.organizationId).then((data) => {
            setFormData(data || {});
            setLoading(false);
        });
    }, []);

    const onFormDataChanged = ({ name, value }) => setFormData({ ...formData, [name]: value });

    const renderField = (fieldConfig, i) => {
        const Component = fieldConfig.isSelect ? Select : fieldConfig.isCheckbox ? Checkbox : Field;
        fieldConfig.dictionary && (fieldConfig.options = props.enums[fieldConfig.dictionary] || []);

        return (
            <Component
                key={i}
                {...fieldConfig}
                i={i}
                value={formData[fieldConfig.inputProps.name]}
                onChange={onFormDataChanged}
                disabled={!policy("user", { user: { roles } }).isAdminWithWriteAccess()}
            />
        );
    };

    const saveForm = () => {
        if (validateForm(config[props.featureModuleType], formData)) {
            setLoading(true);
            saveModuleSettings(props.featureModuleType, props.organizationId, serializeReq(formData)).then(() => {
                setLoading(false);
            });
        } else {
            setIsValid(false);
        }
    };
    const cname = cn("card-body", { "book-form__body--invalid": !isValid });

    const renderFields = () => {
        const { ModuleConfigComponent, ...fields } = config[props.featureModuleType];
        if (ModuleConfigComponent) {
            return (
                <ModuleConfigComponent
                    config={fields}
                    data={formData}
                    onChange={onFormDataChanged}
                    organizationId={Number(props.organizationId)}
                    roles={roles}
                />
            );
        } else {
            return Object.keys(fields).map((fieldKey, i) => renderField(config[props.featureModuleType][fieldKey], i));
        }
    };

    return (
        <div className={cname}>
            {isLoading && (
                <div className="loader-snippet--disable-loader">
                    <Loader />
                </div>
            )}
            {config[props.featureModuleType] ? (
                <>
                    {renderFields()}
                    {policy("user", { user: { roles } }).isAdminWithWriteAccess() && (
                        <div className="btn btn-brand btn-sm" onClick={saveForm}>
                            Сохранить
                        </div>
                    )}
                </>
            ) : (
                <div className="text-center">Для данного модуля настройки не предусмотрены</div>
            )}
        </div>
    );
};

export default FeatureModuleForm;
